import { HttpContext } from "@angular/common/http";
import { Component, OnInit, inject } from "@angular/core";
import {
  ConfirmationService,
  ToastMessageOptions,
  MessageService
} from "primeng/api";
import {
  EntityModelJobDto,
  EntityModelJobExecutionDto,
  ImportAdminControllerService,
  JobsService,
  JobInstanceDto,
  PagedModelEntityModelLogMessageDto,
  EntityModelLogMessageDto,
  RollbackReport,
  RollbackStatusDto
} from "../../../../modules/lipidcompass-data-importer-client";
import { ColumnFilterType, TableColumn } from "../../_models/table-column";
import { LoggingService } from "../../_services/logging.service";
import { FormsModule } from "@angular/forms";
import { TableModule } from "primeng/table";
import { CommonModule, DatePipe, JsonPipe } from "@angular/common";
import { SelectModule } from "primeng/select";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { PaginatorModule } from "primeng/paginator";
import { SubmissionControllerService } from "../../../../modules/lipidcompass-backend-client";
import { RouterModule } from "@angular/router";
import { AccordionModule } from "primeng/accordion";
import { ToastModule } from "primeng/toast";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";

const MZTAB_JOB_NAME = "mzTabResultImportJob";

@Component({
  selector: "app-admin-dataimport-jobs",
  templateUrl: "./admin-dataimport-jobs.component.html",
  styleUrls: ["./admin-dataimport-jobs.component.css"],
  standalone: true,
  imports: [
    AccordionModule,
    ButtonModule,
    CommonModule,
    ConfirmDialogModule,
    DialogModule,
    FormsModule,
    JsonPipe,
    PaginatorModule,
    RouterModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule
  ]
})
export class AdminDataimportJobsComponent implements OnInit {
  private controller = inject(JobsService);
  private importAdminController = inject(ImportAdminControllerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private loggingService = inject(LoggingService);
  protected submissionController = inject(SubmissionControllerService);

  columns: TableColumn[];
  filterFields: string[];
  jobDtos: EntityModelJobDto[] = [];
  selectedJobDto: EntityModelJobDto = null;
  jobInstanceDtos: any[] = [];
  selectedJobInstanceDto: any = null;
  jobExecutions: EntityModelJobExecutionDto[] = [];
  expandedRows = {};

  // Log viewing state
  selectedJobExecutionForLogs:
    | EntityModelJobExecutionDto
    | undefined = undefined;
  jobLogs: PagedModelEntityModelLogMessageDto | undefined = undefined;
  logPageSize: number = 20;
  logCurrentPage: number = 0;
  logSort: string[] = [];

  // Rollback state
  rollbackStatuses: Map<number, RollbackStatusDto> = new Map();
  rollbackReports: Map<number, RollbackReport> = new Map();
  rollbackReportVisible: boolean = false;
  activeRollbackReport: RollbackReport = null;
  activeRollbackExecutionId: number = null;

  ngOnInit() {
    this.columns = [
      {
        field: "name",
        header: "Name",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "jobInstances",
        header: "Job Instances",
        filterType: ColumnFilterType.Object,
        content: value => value,
        visible: true
      }
    ];
    this.filterFields = this.columns.map(value => value.field);
    this.controller.getJobInstances().subscribe(jobInstances => {
      this.loggingService.debug(
        "AdminDataimportJobsComponent",
        "Received job instances " + jobInstances
      );
      this.jobDtos = jobInstances?._embedded?.jobDtoList;
    });
  }

  onRowExpand(event: any) {
    const jobInstance: JobInstanceDto = event.data;
    if (jobInstance?.jobExecutions) {
      for (const exec of jobInstance.jobExecutions) {
        const execId = exec?.executionId;
        if (execId != null && !this.rollbackStatuses.has(execId)) {
          this.loadRollbackStatus(execId);
        }
      }
    }
  }

  loadRollbackStatus(executionId: number) {
    this.importAdminController.getRollbackStatus(executionId).subscribe({
      next: status => {
        this.rollbackStatuses.set(executionId, status);
      },
      error: () => {
        // Silently ignore — user may not have ADMIN role for every view
      }
    });
  }

  isRollbackEligible(execution: any): boolean {
    return execution?.jobName === MZTAB_JOB_NAME;
  }

  wasRolledBack(executionId: number): boolean {
    return this.rollbackStatuses.get(executionId)?.wasRolledBack === true;
  }

  confirmRollback(execution: any) {
    this.confirmationService.confirm({
      header: "Confirm Rollback",
      message: `This will permanently delete all data imported by execution #${execution.executionId} (${execution.jobName}). This action cannot be undone. Proceed?`,
      icon: "pi pi-exclamation-triangle",
      acceptButtonStyleClass: "p-button-danger",
      acceptLabel: "Rollback",
      rejectLabel: "Cancel",
      accept: () => this.executeRollback(execution.executionId)
    });
  }

  executeRollback(executionId: number) {
    this.importAdminController.rollbackJobExecution(executionId).subscribe({
      next: report => {
        this.rollbackReports.set(executionId, report);
        this.rollbackStatuses.set(executionId, {
          jobExecutionId: executionId,
          wasRolledBack: true,
          rollbackSummary: report.warnings?.join("; ")
        });
        this.messageService.add({
          severity: "success",
          summary: "Rollback Complete",
          detail: `Execution #${executionId} rolled back successfully.`
        });
        this.showRollbackReport(executionId, report);
      },
      error: err => {
        const detail =
          err.status === 422
            ? "Only mzTabResultImportJob executions can be rolled back."
            : err.status === 404
            ? "Job execution not found."
            : "Rollback failed. Check server logs for details.";
        this.messageService.add({
          severity: "error",
          summary: "Rollback Failed",
          detail
        });
      }
    });
  }

  showRollbackReport(executionId: number, report?: RollbackReport) {
    this.activeRollbackExecutionId = executionId;
    this.activeRollbackReport =
      report ?? this.rollbackReports.get(executionId) ?? null;
    this.rollbackReportVisible = true;
  }

  expandAll() {
    this.expandedRows = this.jobInstanceDtos.reduce(
      (acc, p) => (acc[p.id] = true) && acc,
      {}
    );
  }

  collapseAll() {
    this.expandedRows = {};
  }

  loadJobLogs(
    jobInstanceDto: JobInstanceDto,
    jobExecutionId: number,
    jobId: number
  ) {
    this.selectedJobExecutionForLogs = jobInstanceDto?.jobExecutions?.find(
      e => e.executionId === jobExecutionId
    );
    this.logCurrentPage = 0;
    this.fetchJobLogs(jobId, this.logCurrentPage, this.logPageSize);
  }

  refreshJobLogs() {
    if (this.selectedJobExecutionForLogs?.jobId) {
      this.fetchJobLogs(
        this.selectedJobExecutionForLogs.jobId,
        this.logCurrentPage,
        this.logPageSize
      );
    }
  }

  fetchJobLogs(jobId: number, page: number, size: number) {
    this.controller.getJobLogs(jobId, page, size, this.logSort).subscribe({
      next: response => {
        this.jobLogs = response;
        const logCount = response?._embedded?.logMessageDtoList?.length || 0;
        if (logCount === 0) {
          this.messageService.add(<ToastMessageOptions>{
            severity: "info",
            summary: "No Logs Available",
            detail: "This job execution has no log messages."
          });
        }
      },
      error: () => {
        this.messageService.add(<ToastMessageOptions>{
          severity: "error",
          summary: "Error",
          detail: "Failed to load job logs"
        });
      }
    });
  }

  onLogPageChange(event: any) {
    if (this.selectedJobExecutionForLogs?.jobId) {
      this.logCurrentPage = Math.floor(event.first / event.rows);
      this.fetchJobLogs(
        this.selectedJobExecutionForLogs.jobId,
        this.logCurrentPage,
        this.logPageSize
      );
    }
  }

  getLogMessageContent(log: EntityModelLogMessageDto): any {
    if (log?._links?.self?.href) {
      const href = log._links.self.href;
      const id = href.substring(href.lastIndexOf("/") + 1);
      return { id, content: "Log message content available" };
    }
    return { id: "unknown", content: "Log message content unavailable" };
  }

  totalDeletedCount(report: RollbackReport): number {
    if (!report?.deletedByCollection) return 0;
    return Object.values(report.deletedByCollection).reduce((a, b) => a + b, 0);
  }

  totalSkippedCount(report: RollbackReport): number {
    if (!report?.skippedByCollection) return 0;
    return Object.values(report.skippedByCollection).reduce(
      (a, b) => a + b.length,
      0
    );
  }

  collectionEntries(
    map: { [key: string]: any } | null | undefined
  ): { key: string; value: any }[] {
    if (!map) return [];
    return Object.entries(map).map(([key, value]) => ({ key, value }));
  }
}
